import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle, Clock } from 'lucide-react';
import PaymentMethodSelector from '../components/PaymentMethodSelector';

const DriverSubscription = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load subscription status');
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/create-subscription`);
      setClientSecret(response.data.client_secret);
      setShowPayment(true);
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize subscription');
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    toast.success('Subscription activated successfully!');
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Subscription cancelled');
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  if (loading && !subscriptionStatus) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">Loading subscription details...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasAccess = subscriptionStatus?.has_access;
  const isFreeAccess = subscriptionStatus?.free_access_granted;
  const subscriptionPrice = subscriptionStatus?.subscription_price || 49.99;

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Driver Subscription</h1>

      {/* Current Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasAccess ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                Subscription Active
              </>
            ) : (
              <>
                <XCircle className="w-6 h-6 text-red-600" />
                No Active Subscription
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isFreeAccess ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-semibold text-blue-900">You have free access granted by admin</p>
              {subscriptionStatus.free_access_until ? (
                <p className="text-sm text-blue-700 mt-1">
                  Valid until: {new Date(subscriptionStatus.free_access_until).toLocaleDateString()}
                </p>
              ) : (
                <p className="text-sm text-blue-700 mt-1">Unlimited free access</p>
              )}
            </div>
          ) : hasAccess ? (
            <div className="space-y-3">
              <p className="text-gray-700">Your subscription is active and you have full access to all driver features.</p>
              {subscriptionStatus.subscription_end_date && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Renews on: {new Date(subscriptionStatus.subscription_end_date).toLocaleDateString()}
                </p>
              )}
              <Button variant="outline" onClick={handleCancelSubscription} className="mt-4">
                Cancel Subscription
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-red-700 font-semibold">⚠️ You don't have an active subscription</p>
              <p className="text-gray-700">Subscribe now to access all driver features including:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Create and manage rides</li>
                <li>Claim available rides</li>
                <li>View ride requests from passengers</li>
                <li>Track your earnings and statistics</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Pricing */}
      {!hasAccess && !showPayment && (
        <Card className="border-2 border-green-500">
          <CardHeader className="bg-green-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Driver Monthly Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-green-600 mb-2">
                ${subscriptionPrice}
                <span className="text-2xl text-gray-600">/month</span>
              </div>
              <p className="text-gray-600">Automatic monthly billing</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Full access to all driver features</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Create unlimited rides</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Claim passenger ride requests</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Track earnings and analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Cancel anytime</span>
              </div>
            </div>

            <Button 
              onClick={handleSubscribe} 
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Subscribe Now'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment Form */}
      {showPayment && (
        <Card>
          <CardHeader>
            <CardTitle>Complete Your Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <PaymentMethodSelector
              amount={subscriptionPrice}
              description="Onyx Reserve Driver Monthly Subscription"
              packageType="driver_subscription"
              onSuccess={handlePaymentSuccess}
              onError={(error) => {
                console.error('Subscription payment error:', error);
                toast.error('Subscription payment failed. Please try again.');
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverSubscription;
